import React from 'react';
import CollapsibleList from 'components/CollapsibleList/CollapsibleList';
import CollapsibleSection from 'components/CollapsibleList/CollapsibleSection';
import { Blocks, MonitorCog, BookCheck } from 'lucide-react';
import CollapsibleItem from 'components/CollapsibleList/CollapsibleItem';
import propTypes from 'prop-types';
import Button from 'tailwind-components/Button/Button';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { cn } from 'lib/utils';
import { areOnboardingInvitesDisabled } from 'helpers/viewing-rules';
import { ONEILL_MAIN_AGENCY_ID } from 'constants/enum';
import { isMobileWebView, postMobileNavigate } from 'helpers/mobileWebView';
import { makeAgencyNodeId } from 'helpers/agencyManagement/agency-tree-search';
import {
  agencyRowListProps,
  agencyTitleAnchorProps
} from 'tailwind-components/AgencyTreeFindBar/agencyTreeFindDom';

export const AGENCY_TREE_NODE_ID_PREFIX = 'at';

/**
 * Builds the Dashboard / Settings / Appointments Center options for a given
 * agency. On mobile (isMobileWebView), each option's onClick prevents the
 * in-webview Link navigation and instead asks the native app to switch to
 * its own screen for that agency, since these are separate native screens.
 */
function buildAgencyOptions(agencyId, invitesDisabled) {
  const routes = [
    { text: 'Dashboard', icon: <Blocks size={20} />, path: 'dashboard' },
    {
      text: invitesDisabled ? 'Settings' : 'Settings/Invites',
      icon: <MonitorCog size={20} />,
      path: 'settings'
    },
    {
      text: 'Appointments Center',
      icon: <BookCheck size={20} />,
      path: 'appointments'
    }
  ];

  return routes.map(({ text, icon, path }) => {
    const to = `/agency/${path}/${agencyId}`;
    return {
      text,
      icon,
      to,
      onClick: isMobileWebView()
        ? e => {
            e.preventDefault();
            postMobileNavigate(to);
          }
        : undefined
    };
  });
}

const renderChilds = (Member_Agencies, invitesDisabled, idPath = 'r') => {
  if (!Member_Agencies || Member_Agencies.length === 0) return null;

  return Member_Agencies.map((agency, index) => {
    const nextPath = `${idPath}p${index}`;
    const nodeId = makeAgencyNodeId(
      AGENCY_TREE_NODE_ID_PREFIX,
      nextPath,
      agency.AgencyID
    );
    const hasChildren =
      Array.isArray(agency.Member_Agencies) &&
      agency.Member_Agencies.length > 0;

    if (hasChildren) {
      return (
        <CollapsibleSection
          key={nodeId}
          id={nodeId}
          title={agency.AgencyName}
          highlightFirst={agency.AgencyID === ONEILL_MAIN_AGENCY_ID}
          titleColor='secondary'
          expanded
          uncontrolled
          titleAnchorProps={agencyTitleAnchorProps(agency.AgencyID)}
          listItemProps={agencyRowListProps(agency.AgencyID)}
          optionsArray={buildAgencyOptions(agency.AgencyID, invitesDisabled)}
        >
          {renderChilds(agency.Member_Agencies, invitesDisabled, nextPath)}
        </CollapsibleSection>
      );
    }

    return (
      <CollapsibleItem
        key={nodeId}
        title={agency.AgencyName}
        titleAnchorProps={agencyTitleAnchorProps(agency.AgencyID)}
        listItemProps={agencyRowListProps(agency.AgencyID)}
        optionsArray={buildAgencyOptions(agency.AgencyID, invitesDisabled)}
      />
    );
  });
};

function AgencyHierarchyList({ data }) {
  const userAgencyData = useSelector(state => state.user.zohoData.agencyData);
  const invitesDisabled = areOnboardingInvitesDisabled(userAgencyData);

  if (data.length === 1 && data[0]?.Member_Agencies?.length === 0) {
    const agency = data[0];

    return (
      <div
        data-agency-id={agency.AgencyID}
        className={cn(
          'flex h-full flex-col items-center justify-center gap-2 rounded-lg',
          'text-center'
        )}
      >
        <h3
          className='m-0 text-xl sm:text-2xl font-semibold text-primary-darkened'
          {...agencyTitleAnchorProps(agency.AgencyID)}
        >
          {agency.AgencyName}
        </h3>
        <p className='m-0 text-sm text-grey'>
          Manage your agency dashboard and settings
        </p>
        <div className='mt-3 flex flex-wrap items-center justify-center gap-3'>
          <Link
            to={`/agency/dashboard/${agency.AgencyID}`}
            replace
            onClick={
              isMobileWebView()
                ? e => {
                    e.preventDefault();
                    postMobileNavigate(`/agency/dashboard/${agency.AgencyID}`);
                  }
                : undefined
            }
          >
            <Button
              variant='primaryLight'
              size='small'
              rounded='medium'
              text='Go to Dashboard'
              icon={<Blocks size={20} />}
            />
          </Link>
          <Link
            to={`/agency/settings/${agency.AgencyID}`}
            replace
            onClick={
              isMobileWebView()
                ? e => {
                    e.preventDefault();
                    postMobileNavigate(`/agency/settings/${agency.AgencyID}`);
                  }
                : undefined
            }
          >
            <Button
              variant='primaryLight'
              size='small'
              rounded='medium'
              text={
                invitesDisabled ? 'Go to Settings' : 'Go to Settings & Invites'
              }
              icon={<MonitorCog size={20} />}
            />
          </Link>
          <Link
            to={`/agency/appointments/${agency.AgencyID}`}
            replace
            onClick={
              isMobileWebView()
                ? e => {
                    e.preventDefault();
                    postMobileNavigate(
                      `/agency/appointments/${agency.AgencyID}`
                    );
                  }
                : undefined
            }
          >
            <Button
              variant='primaryLight'
              size='small'
              rounded='medium'
              text='Go to Appointments Center'
              icon={<BookCheck size={20} />}
            />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className='min-w-0 flex-1 rounded-lg'>
      <CollapsibleList>
        {data.length > 0 && renderChilds(data, invitesDisabled, 'r')}
      </CollapsibleList>
    </div>
  );
}

AgencyHierarchyList.propTypes = {
  data: propTypes.array
};

/**
 * Memoized: with large downlines (500+ agencies) re-rendering the tree is the
 * single most expensive operation on this page, so it only re-renders when the
 * tree data itself changes.
 */
export default React.memo(AgencyHierarchyList);
